import _ from 'lodash';
import { estimateTax } from '@/apis/tax';
import { getCurrency } from "@/utils/currency";
import {getDefaultCvvReAuthInfo} from "@/store/modules/payment";
import { BILLING_CYCLE, NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP } from '@/common/constants';
import { isEmptyVal } from '@/utils';
import { isFromCancelCampaign } from '@/views/config/coupon/helper/utils';
import { compareDate, getNextPstCycleDate } from '@/utils/dateformat';
import { plansof } from '@/utils/chain';
import { getRealPlanCode } from '@/common/service/parse';

const state = {
  taxes: [],
  isTaxEstimatedBefore: false,
  isTaxUpdateAttempted: false,
  isLoadingTax: false,
  isTaxEstimatedSuccess: false,
}

const getters = {
  getTaxes: (state) => state.taxes,
  isTaxEstimatedBefore: state => state.isTaxEstimatedBefore,
  isTaxUpdateAttempted: state => state.isTaxUpdateAttempted,
  isLoadingTax: state => state.isLoadingTax,
  getPlanCodeWithTaxInfo(state) {
    return state.taxes.reduce((acc, x) => {
      const planCode = x?.planCode;
      if (planCode) acc[planCode] = x;
      return acc;
    }, {});
  },
  getPlancodeMapTax(state) {
    return state.taxes.reduce((acc, x) => {
      acc[x.planCode] = x.tax.reduce((amount, y) => {
        return amount + y.taxAmount;
      }, 0);
      return acc;
    }, {});
  },
  getSelectedPlanMapList4BillingCycle(state, getter) {
    const { getSelectedPlans } = getter;

    return {
      [BILLING_CYCLE.MONTHLY]: plansof(getSelectedPlans).cycle(BILLING_CYCLE.MONTHLY).get(),
      [BILLING_CYCLE.ANNUAL]: plansof(getSelectedPlans).cycle(BILLING_CYCLE.ANNUAL).get(),
      [BILLING_CYCLE.QUARTERLY]: plansof(getSelectedPlans).cycle(BILLING_CYCLE.QUARTERLY).get(),
      [BILLING_CYCLE.TWO_YEARS]: plansof(getSelectedPlans).cycle(BILLING_CYCLE.TWO_YEARS).get(),
      [BILLING_CYCLE.THREE_YEARS]: plansof(getSelectedPlans).cycle(BILLING_CYCLE.THREE_YEARS).get()
    }
  },
  getCycledTaxTotalAmount(state, getter) {
    const { getPlancodeMapTax, getSelectedPlanMapList4BillingCycle } = getter;

    return Object.keys(getSelectedPlanMapList4BillingCycle)
      .reduce((pre, billingCycle) => {
        const plans = getSelectedPlanMapList4BillingCycle[billingCycle];

        pre[billingCycle] = plans.reduce((acc, x) => {
          const planCode = getRealPlanCode(x);
          const amount = getPlancodeMapTax[planCode] ?? 0;
          return acc + amount;
        }, 0);

        return pre;
      }, {});
  },
  getNextCycledTaxTotalAmount(state, getter) {
    // switch feature
    if (window.$isEnabled?.("useLegacyComputedTaxInNext")) {
      return getter.getCycledTaxTotalAmount;
    }

    const { discountPlans: planWithDiscount } = getter.discountWithAmountMap;

    // if not have coupon, will use current tax total amount
    if (isEmptyVal(planWithDiscount)) {
      return getter.getCycledTaxTotalAmount;
    }

    const { getPlancodeMapTax, getPlanCodeWithTaxInfo, getSelectedPlansAmount, getSelectedPlanMapList4BillingCycle } = getter;
    const planAmountWithPlanCode = getSelectedPlansAmount.plans;

    return Object.keys(getSelectedPlanMapList4BillingCycle)
      .reduce((pre, billingCycle) => {
        const plans = getSelectedPlanMapList4BillingCycle[billingCycle] ?? [];

        pre[billingCycle] = plans.reduce((acc, x) => {
          const planCode = getRealPlanCode(x);
          const couponExpireDate = _.get(planWithDiscount, [planCode, 'expireDate']);
          // set PDT offset -7
          const nextPstCycleDateInfo = getNextPstCycleDate({ offset: -7 });
          const nextCycleKey = NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP[billingCycle];
          const discountedPlanAmount = getPlanCodeWithTaxInfo[planCode]?.chargeAmount ?? 0;
          const originalPlanAmount = planAmountWithPlanCode[planCode];
          const currentTaxAmount = getPlancodeMapTax[planCode] ?? 0;

          // current plan amount should more than zero & coupon will expire in next billing cycle
          if (
            discountedPlanAmount
            && originalPlanAmount
            && couponExpireDate
            && !compareDate(couponExpireDate, nextPstCycleDateInfo[nextCycleKey])
          ) {
            acc += (originalPlanAmount/discountedPlanAmount) * currentTaxAmount;
          } else {
            acc += currentTaxAmount;
          }

          return acc;
        }, 0)

        return pre;
      }, {});
  },
  getTaxTotalAmount: (state, getters) => {
    if (!getters.getSelectedPlans.length) return 0;
    if (getters.isInrCitEligible && getters.isProratedChargeAmountCalculated) return getters.getProratedChargeTaxAmount;
    return state.taxes.reduce((acc, x) => {
      const planTax = x.tax.reduce((acc, y) => {
        return acc + y.taxAmount;
      }, 0);
      return acc + planTax;
    }, 0);
  },
  timebasedDiscountPlanEdit: (state, getters) => {
    const { getSelectedPlansWithQuantityMap, couponState, fromCampaignLink } = getters;

    if (window.$context?.is?.edit && !isEmptyVal(couponState?.timeBase) && !fromCampaignLink) {
      const selectedPlanCodeList = Object.keys(getSelectedPlansWithQuantityMap);

      return _.some(couponState.timeBase, (p) => _.includes(selectedPlanCodeList, p?.plan_code));
    }

    return false;
  },
  taxPayload: (state, getters) => {
    const { soldToContact, selectedPlanListPayLoad, couponState, isTaxExempt } = getters;

    return {
      contact: [soldToContact],
      taxExempt: isTaxExempt,
      currency: getCurrency(),
      orderData: {
        planList: selectedPlanListPayLoad,
        coupon: {
          fromCampaignLink: couponState?.fromCampaignLink ?? false,
          code: couponState?.couponCode || '',
        },
      },
      extension: {
        jointsaleCode: getters.partnerRefer?.jointsaleCode,
      },
      timebasedDiscountPlanEdit: getters.timebasedDiscountPlanEdit,
      ...(isFromCancelCampaign() ? { oldPlanType: window.$config("oldPlanType") } : {}),
    };
  }
}

const mutations = {
  setTaxes(state, data) {
    state.taxes = data;
  },
  setIsTaxEstimatedBefore(state, is){
    state.isTaxEstimatedBefore = is;
  },
  setIsTaxUpdateAttempted(state, is){
    state.isTaxUpdateAttempted = is;
  },
  setIsLoadingTax(state, is) {
    state.isLoadingTax = is;
  },
  setIsTaxEstimatedSuccess(state, is) {
    state.isTaxEstimatedSuccess = is;
  }
}

const actions = {
  async estimateTax({ commit, getters }) {
    commit('setTaxes', []);
    const { taxPayload, paymentMethodUpdated } = getters;
    try {
      const { result, status } = await estimateTax(taxPayload);
      commit('setIsTaxEstimatedBefore', true);
      if (!status) return;
      commit('setIsTaxEstimatedSuccess', true);
      const { taxEstimate = [], cvvReAuth = getDefaultCvvReAuthInfo()} = result;
      commit('setTaxes', taxEstimate);
      if (!paymentMethodUpdated) {
        commit('setCvvReAuthInfo', cvvReAuth);
      }
    } catch (e) {
      commit('setIsTaxEstimatedSuccess', false);
    }
  }
}

export default {
  state,
  getters,
  mutations,
  actions,
}
